import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Flame, Store } from "lucide-react";
import { QrMedia } from "../public/qr/QrMedia";
import { BkBanner, useBannerColors } from "./BkBanner";
import { ItemCard } from "./ItemCards";
import { SiteFooter } from "./SiteFooter";

/** Compact dietary filter driven by the existing `veg` classification. Veg-only menus show an indicator only. */
export const VegFilter = ({ value, onChange, hasNonVeg, testId = "veg-filter" }) => {
  if (!hasNonVeg) return <span className="sig-veg-badge" data-testid={`${testId}-vegonly`}><Leaf size={14} />Pure Veg</span>;
  return (
    <div className="sig-veg" role="radiogroup" aria-label="Dietary filter" data-testid={testId}>
      {[["all", "All"], ["veg", "Veg"], ["nonveg", "Non-Veg"]].map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} className={`${value === k ? "active" : ""} ${k}`} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}>{k !== "all" && <i />}{l}</button>
      ))}
    </div>
  );
};
export const vegMatch = (it, f) => f === "all" || (f === "veg" ? it.veg !== false : it.veg === false);

const Row = ({ items, qtyOf, ctrl, prefix }) => (
  <div className="sig-row" data-testid={`${prefix}-row`}>{items.map((it) => <div className="sig-row-card" key={it.id}><ItemCard it={it} qty={qtyOf[it.id] || 0} ctrl={ctrl} testPrefix={prefix} /></div>)}</div>
);

/** Signature homepage: banner → veg filter → 3-col/2-row category grid (swipe for more) → Explore → Recommended → Hot Selling → brick Brand Wall → footer. */
export const HomeSig = ({ slug, site, branch, menu, ctrl, qtyOf, onBanner, sheetsOpen, veg, setVeg }) => {
  const items = menu.items || [];
  const banners = menu.banners || [];
  const colors = useBannerColors(banners, "#ffffff");
  const [, setBg] = useState(null);
  const catsRef = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const visible = useMemo(() => items.filter((i) => vegMatch(i, veg)), [items, veg]);
  const cats = (menu.categories || []).filter((c) => visible.some((i) => i.category_id === c.id));
  const pick = (ids) => ids.map((id) => visible.find((i) => i.id === id)).filter((i) => i && i.is_available);
  const recommended = pick(menu.recommended_item_ids || []);
  const hot = pick(menu.hot_selling_item_ids || []).filter((i) => !recommended.some((r) => r.id === i.id) || true);
  const wb = menu.brand_wall_banner || {};
  const logo = wb.show_logo !== false ? (wb.logo_url || site.brand.logo_url) : null;
  return (
    <div className="sig-home" data-testid="home-signature">
      {banners.length > 0 && <div className="sig-banner" data-testid="home-banner"><BkBanner banners={banners} onOpen={onBanner} paused={sheetsOpen} colors={colors} onActiveColor={setBg} /></div>}
      <div className="os-section" style={{ paddingTop: 14 }}><VegFilter value={veg} onChange={setVeg} hasNonVeg={!!menu.has_non_veg} /></div>

      {cats.length > 0 && (
        <section className="os-section" data-testid="home-our-menu">
          <h2 className="sig-title">Explore our categories</h2>
          <div ref={catsRef} className={`sig-cats ${cats.length <= 3 ? "sig-cats-single" : ""}`} data-testid="sig-category-scroller">
            {cats.map((c) => (
              <Link key={c.id} to={`/order/${slug}/menu/${c.id}`} className="sig-cat" draggable={false} data-testid={`home-category-${c.id}`}>
                <span className="sig-cat-img"><QrMedia src={c.image_url} alt={c.name} size="large" /></span>
                <span className="sig-cat-name">{c.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="os-section"><Link to={`/order/${slug}/menu`} className="os-cta" data-testid="home-explore-menu">Explore Full Menu <ArrowRight size={18} /></Link></section>

      {recommended.length > 0 && <section className="os-section" data-testid="home-recommended"><h2 className="sig-title">Recommended for you</h2><Row items={recommended} qtyOf={qtyOf} ctrl={ctrl} prefix="rec-item" /></section>}
      {hot.length > 0 && <section className="os-section" data-testid="home-hot-selling"><h2 className="sig-title"><Flame size={14} style={{ color: "#ea580c" }} /> Hot selling</h2><Row items={hot} qtyOf={qtyOf} ctrl={ctrl} prefix="hot-item" /></section>}

      {menu.brand_wall_published && (
        <section className="os-section" data-testid="home-brand-wall">
          <Link to={`/order/${slug}/wall`} className="sig-brick" data-testid="home-brand-wall-link">
            <span className="sig-brick-label" data-testid="brand-wall-label">{wb.label || "Our Brand Wall"}</span>
            {logo ? <img src={logo} alt="" className="sig-brick-logo" data-testid="brand-wall-logo" /> : <span className="sig-brick-logo sig-brick-fallback"><Store size={40} /></span>}
            {wb.show_name !== false && <span className="sig-brick-name" data-testid="brand-wall-name">{site.brand.name}</span>}
          </Link>
        </section>
      )}
      <SiteFooter brand={site.brand} branch={branch} website={menu.website} large />
    </div>
  );
};
